/* eslint-disable @typescript-eslint/no-explicit-any -- parsing json schema */
import type { RJSFSchema, UiSchema } from "@rjsf/utils";

interface Step {
  json_schema: RJSFSchema;
  ui_schema: UiSchema;
  name: string;
  data?: Record<string, unknown>;
  prefilled_data?: Record<string, unknown>;
}

export interface PatchMetadata {
  isPhoneOtp?: boolean;
  isManualApplication?: boolean;
}

interface Patch {
  shouldApply: (metaData: PatchMetadata) => boolean;
  applyPatch: (step: {
    jsonSchema: RJSFSchema;
    uiSchema: UiSchema;
    data?: Record<string, unknown>;
    prefilledData?: Record<string, unknown>;
    metaData: PatchMetadata;
  }) => {
    jsonSchema: RJSFSchema;
    uiSchema: UiSchema;
    data: Record<string, unknown>;
    prefilledData: Record<string, unknown>;
    metaData: PatchMetadata;
  };
}

type StepName = string;
type PatchName = string;

/** The BE's `money_property` shape is `{ cents, currency }` */
const isMoneySchema = (schema: any): boolean =>
  schema?.type === "object" &&
  Boolean(schema.properties?.cents) &&
  Boolean(schema.properties?.currency);

const getRenderedProperties = (schema: any): Record<string, any>[] =>
  [
    schema?.properties,
    ...(Array.isArray(schema?.allOf)
      ? schema.allOf.map((branch: any) => branch?.then?.properties)
      : [])
  ].filter(Boolean);

/** Deletes money fields that the uiSchema hides so RJSF does not validate them */
const stripHiddenMoneyFields = (schema: any, uiSchema: any): void => {
  if (!schema || typeof schema !== "object") return;

  if (schema.type === "array" && schema.items) {
    uiSchema.items = uiSchema.items || {};
    stripHiddenMoneyFields(schema.items, uiSchema.items);
    return;
  }

  const properties = getRenderedProperties(schema);
  properties.forEach((properties) => {
    Object.entries(properties).forEach(([key, propertySchema]) => {
      if (
        isMoneySchema(propertySchema) &&
        uiSchema[key]?.["ui:widget"] === "hidden"
      ) {
        delete properties[key];
        delete uiSchema[key];
      }

      uiSchema[key] = uiSchema[key] || {};
      stripHiddenMoneyFields(propertySchema, uiSchema[key]);
    });
  });
};

const GLOBAL_PATCHES: Record<PatchName, Patch> = {
  moneyFields: {
    /** AX handles these fields on its own, but PX relies on RJSF */
    shouldApply: (metaData) => Boolean(metaData.isManualApplication),
    applyPatch: ({
      jsonSchema,
      uiSchema,
      data = {},
      prefilledData = {},
      metaData
    }) => {
      const clonedJsonSchema = structuredClone(jsonSchema);
      const clonedUiSchema = structuredClone(uiSchema);

      stripHiddenMoneyFields(clonedJsonSchema, clonedUiSchema);

      return {
        jsonSchema: clonedJsonSchema,
        uiSchema: clonedUiSchema,
        data,
        prefilledData,
        metaData
      };
    }
  }
};

// Unfortunately, some of the fields must be controlled by FE due to much easier way of extending their functionality on the FE side
const STEPS_TO_PATCH: Record<StepName, Record<PatchName, Patch>> = {
  personal_details: {
    // Control whether 'phone' or 'email' should be a required field, depending on used OTP method
    requiredPhoneOrEmail: {
      shouldApply: (_metaData) => true,
      // @ts-expect-error - rjsf types are bs
      applyPatch: ({
        jsonSchema,
        uiSchema,
        data,
        prefilledData,
        metaData
      }: {
        jsonSchema: {
          properties?: {
            contact_phone_number?: {
              minLength?: number;
            };
          };
          required: string[];
        };
        uiSchema: {
          contact_email: Record<string, unknown>;
          contact_phone_number: Record<string, unknown>;
        };
        data: {
          contact_email?: string | null;
          contact_first_name?: string;
          contact_last_name?: string;
          contact_middle_name?: string | null;
          contact_phone_number?: string | null;
        };
        prefilledData: {
          contact_email: string | null;
          contact_first_name: string;
          contact_last_name: string;
          contact_middle_name?: string | null;
          contact_phone_number: string | null;
        };
        metaData: PatchMetadata;
      }) => {
        const { isPhoneOtp, isManualApplication } = metaData;

        const clonedUiSchema = structuredClone(uiSchema);
        const clonedJsonSchema = structuredClone(jsonSchema);
        const clonedData = structuredClone(data);
        const clonedPrefilledData = structuredClone(prefilledData);

        // If middle_name in prefilled data is null OR in data --> we replace it with undefined
        if (clonedPrefilledData.contact_middle_name === null) {
          clonedPrefilledData.contact_middle_name = undefined;
        } else if (clonedData.contact_middle_name === null) {
          clonedData.contact_middle_name = undefined;
        }

        clonedJsonSchema.required.push(
          isPhoneOtp ? "contact_phone_number" : "contact_email"
        );
        // manual app
        // email & phone -> required + editable
        if (isManualApplication) {
          clonedUiSchema.contact_email["ui:readonly"] = false;
          clonedJsonSchema.required.push("contact_phone_number");

          if (clonedJsonSchema.properties?.contact_phone_number)
            clonedJsonSchema.properties.contact_phone_number.minLength = 1;

          return {
            jsonSchema: clonedJsonSchema,
            uiSchema: clonedUiSchema,
            data: clonedData,
            prefilledData: clonedPrefilledData
          };
        }

        if (isPhoneOtp) {
          clonedUiSchema.contact_phone_number["ui:readonly"] = true;
          clonedUiSchema.contact_email["ui:readonly"] = true;

          clonedJsonSchema.required.push("contact_email");
        } else {
          clonedUiSchema.contact_email["ui:readonly"] = true;
          clonedUiSchema.contact_phone_number["ui:readonly"] = true;
          clonedJsonSchema.required.push("contact_phone_number");

          if (clonedJsonSchema.properties?.contact_phone_number)
            clonedJsonSchema.properties.contact_phone_number.minLength = 1;

          if (
            clonedData.contact_phone_number === null ||
            clonedData.contact_phone_number === undefined
          ) {
            clonedData.contact_phone_number =
              prefilledData.contact_phone_number ?? "";

            // Prefill current data with other data.
            // this will is need and will be executed once
            // when customer has never submitted basic info
            if (!clonedData.contact_email) {
              clonedData.contact_email = prefilledData.contact_email;
              clonedData.contact_first_name = prefilledData.contact_first_name;
              clonedData.contact_last_name = prefilledData.contact_last_name;
              clonedData.contact_middle_name =
                prefilledData.contact_middle_name;
            }
          }
        }

        return {
          jsonSchema: clonedJsonSchema,
          uiSchema: clonedUiSchema,
          data: clonedData,
          prefilledData: clonedPrefilledData
        };
      }
    }
  },
  /* Manual apps don't ask "do you know home details", since the PM filling out the form is expected to know them */
  home_details: {
    hideIsKnownGate: {
      shouldApply: (metaData) => Boolean(metaData.isManualApplication),
      applyPatch: ({
        jsonSchema,
        uiSchema,
        data = {},
        prefilledData = {},
        metaData
      }) => {
        const clonedUiSchema = structuredClone(uiSchema);

        clonedUiSchema.is_known = {
          ...clonedUiSchema.is_known,
          "ui:widget": "hidden"
        };

        return {
          jsonSchema,
          uiSchema: clonedUiSchema,
          data: Object.keys(data).length ? { ...data, is_known: true } : data,
          prefilledData: { ...prefilledData, is_known: true },
          metaData
        };
      }
    }
  }
};

export const patchApplicantStep = ({
  step,
  patchMeta
}: {
  step: Step;
  patchMeta: PatchMetadata;
}) => {
  const {
    jsonSchema = step.json_schema,
    uiSchema = step.ui_schema,
    data = step.data,
    prefilledData = step.prefilled_data
  } = Object.entries({
    ...GLOBAL_PATCHES,
    ...(STEPS_TO_PATCH[step.name || ""] || {})
  }).reduce(
    (ret, [_patchName, patch]) =>
      patch.shouldApply(patchMeta) ? patch.applyPatch(ret) : ret,
    {
      jsonSchema: step.json_schema,
      uiSchema: step.ui_schema,
      data: step.data || {},
      prefilledData: step.prefilled_data || {},
      metaData: patchMeta
    }
  );

  return {
    jsonSchema,
    uiSchema,
    data,
    prefilledData
  };
};
